import React from 'react';
import { Alert, Button, Stack, Text, Center, Container } from '@mantine/core';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';

/**
 * Catch-all boundary at the app root. Mantine doesn't ship one, so this
 * is a thin class-component wrapper that intercepts render errors anywhere
 * in the tree and shows a recoverable error card instead of a blank page.
 *
 * Hot path: a thrown React error during render → catch → display the error
 * with a "Reload" button. Errors surfaced by user actions (e.g. failed API
 * calls) are NOT routed here — those should show toasts via mantine's
 * notifications.
 */
interface State {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  reset = () => {
    this.setState({ error: null });
  };

  reload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <Container size="sm" mt="xl">
        <Center>
          <Alert
            icon={<IconAlertTriangle size={20} />}
            title="Something went wrong"
            color="red"
            variant="light"
            radius="md"
            w="100%"
          >
            <Stack gap="sm">
              <Text size="sm">{this.state.error.message || 'Unexpected error.'}</Text>
              {this.state.error.stack && (
                <Text size="xs" c="dimmed" component="pre" style={{ whiteSpace: 'pre-wrap', overflow: 'auto', maxHeight: 200 }}>
                  {this.state.error.stack}
                </Text>
              )}
              <Stack gap="xs" align="flex-start">
                <Button size="xs" onClick={this.reset}>Try again</Button>
                <Button size="xs" variant="default" leftSection={<IconRefresh size={14} />} onClick={this.reload}>
                  Reload page
                </Button>
              </Stack>
            </Stack>
          </Alert>
        </Center>
      </Container>
    );
  }
}
